import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { User } from '../../../models/users/user';
import { FloatLabelType } from '@angular/material/form-field';
import { NavService } from '../../../models/ui/navService';
import { UserService } from '../../../models/users/userService';
import { FormMode, UserRoleHierarchy } from '../../../core/constants';
import { AccessManager } from '../../ui/access-manager/access-manager';
import { StorageService } from '../../../core/storage/storageService';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { TextInputComponent } from '../../ui/controls/text-input/text-input.component';
import { DdlInputComponent } from '../../ui/controls/ddl-input/ddl-input.component';
import {
  FormValidator,
  ValidationMessage,
} from '../../../models/validation/formValidator';
import { iValidatedControl } from '../../ui/controls/iValidatedControl';
import { UserFormComponent } from '../user-form/user-form.component';
import { BaseComponent } from '../../baseComponent';
import { Page } from '../../ui/filterable-table/tableViewAbstractComponent';
import { MerchantTableViewComponent } from '../../merchant/merchant-table-view/merchant-table-view.component';
import { SearchFilters } from '../../../models/search/searchFilters';
import { TableView } from '../../ui/filterable-table/tableView';
import { Options } from '../../ui/filterable-table/options';
import { ListItem } from '../../../../../../../libs/Shared/components/model/listItem';
import { UserRole } from '../../../../../../../libs/Shared/components/model/UserRoles';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  standalone: false,
})
export class EditUserComponent
  extends BaseComponent
  implements AfterViewInit, OnChanges
{
  @Input() user: Partial<User> | undefined;
  @Input() enableEditing: boolean = false; // shows the Edit button
  @Input() enableSaving: boolean = false; // shows the save/cancel/submit buttons
  @Input() merchantId: string | undefined;
  @Input() enableClose: boolean = true; // Adds master Cancel button for leaving the Edit User view
  @Input() enableSelectExistingUser: boolean = false;
  @Input() enableRoleSelection: boolean = true;
  @Input() initialMode: FormMode = FormMode.View;
  @Output() saved = new EventEmitter<any>(); // Partial<User>
  @Output() created = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter();

  @ViewChild('userForm') userForm: UserFormComponent | undefined;
  @ViewChildren(TextInputComponent) validatedTextBoxes:
    | QueryList<TextInputComponent>
    | undefined;
  @ViewChildren(DdlInputComponent) validatedDropDowns:
    | QueryList<DdlInputComponent>
    | undefined;
  @ViewChild('merchantTable') merchantTableControl:
    | MerchantTableViewComponent
    | undefined;

  merchantsTableView: any;

  merchantsTableOptions: Options = new Options({
    allowFavorites: false,
    restrictToMyMerchants: false,
    selectAction: 'none',
    paginated: true,
    filterable: true,
    hiddenFields: ['contact', 'status'],
    allowEditing: true,
  });

  _currentMode: FormMode = this.initialMode;

  allNamespaces: any[] = [];

  allowRoleEdit: boolean = false;
  allowImpersonate: boolean = false; // permission to impersonate any user
  allowImpersonateThisUser: boolean = false;

  impersonateNotAllowedReason = '';

  //userFormData: Partial<User> = User.createEmpty();   // copied from passed-in user object

  selectedRole: UserRole | undefined;
  userAccessGrant: string[] = [];
  userAccessRevoke: string[] = [];

  sourceRolesUser: string = '';

  categoryMap = new Array();
  userAccessMap: Map<string, boolean> | undefined = undefined;
  allAccessBitsToCheck: any[] = [];
  accessCategories: string[] = [];
  usedAccessCategories: string[] = [];

  // roleOptions: ListItem[] = CacheService.roleList;
  roleOptions: ListItem<UserRole>[] = [];

  userPermsViewMode: FormMode = this.initialMode;
  userOperatorAccessLevel: number | undefined;

  public get formMode(): typeof FormMode {
    return FormMode;
  }

  constructor(
    private navService: NavService,
    private userService: UserService,
    private storageService: StorageService,
    private access: AccessManager,
    private uiErrorHandler: UIErrorHandler,
    private formValidator: FormValidator,
    private changeDetector: ChangeDetectorRef,
  ) {
    super();

    this.merchantsTableView = new TableView(MerchantTableViewComponent);

    //this.roleOptions = CacheService.roleList;
    this.roleOptions = [
      new ListItem({ id: 'portal.admin', name: 'Administrator' }),
      new ListItem({ id: 'portal.sales', name: 'Sales team' }),
      new ListItem({ id: 'portal.support', name: 'Success team' }),
      new ListItem({ id: 'portal.manager', name: 'Manager' }),
      new ListItem({ id: 'portal.merchant', name: 'MID Owner' }),
    ];

    //Filter out roles that are higher than the current user's role
    this.roleOptions = this.roleOptions.filter(
      (role) =>
        UserRoleHierarchy[role.id as keyof typeof UserRoleHierarchy] <=
        UserRoleHierarchy[
          this.storageService.getCurrentUser()
            ?.originalRole as keyof typeof UserRoleHierarchy
        ],
    );

    this.access
      .getUserAccess([
        AccessManager.AUTH_MANAGE_ALL_USERS,
        AccessManager.AUTH_UPDATE_MERCHANT_USERS,
        AccessManager.AUTH_IMPERSONATE_USER,
      ])
      .then((accessRecord) => {
        this.allowRoleEdit =
          accessRecord[AccessManager.AUTH_MANAGE_ALL_USERS] ||
          accessRecord[AccessManager.AUTH_UPDATE_MERCHANT_USERS];
        this.allowImpersonate =
          accessRecord[AccessManager.AUTH_IMPERSONATE_USER];

        if (!this.allowImpersonate) {
          this.allowImpersonateThisUser = false;
          this.impersonateNotAllowedReason =
            'You do not have permission to impersonate users.';
        } else if (this.storageService.getImpersonatingUser() != null) {
          this.allowImpersonateThisUser = false;
          this.impersonateNotAllowedReason =
            'You cannot impersonate a user while impersonating another user.';
        }
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });

    // TODO: Build this dynamically
    // this.roleOptions = [
    //     new ListItem({id: "portal.admin", name:"Administrator"}),
    //     new ListItem({id: "portal.support", name:"Success team"}),
    //     new ListItem({id: "portal.manager", name:"Manager"}),
    //     new ListItem({id: "portal.merchant", name:"MID Owner"})
    // ]

    this.userOperatorAccessLevel = storageService.getCurrentUser()?.accessLevel;
  }

  public getData(): Partial<User> | undefined {
    return this.userForm?.getData();
  }

  public validate(): ValidationMessage[] {
    let errors: ValidationMessage[] = [];
    let validatedControls: iValidatedControl[] = [];
    if (this.validatedTextBoxes && this.validatedDropDowns) {
      validatedControls = validatedControls.concat(
        this.validatedTextBoxes.toArray(),
      );
      validatedControls = validatedControls.concat(
        this.validatedDropDowns.toArray(),
      );
      errors = this.formValidator.validate(validatedControls);
    }

    return errors;
  }

  ///// Page lifecycle

  ngAfterViewInit() {
    this.allNamespaces = StorageService.getAllAccessNamespaces();
    this._currentMode = this.initialMode;

    this.reloadUserAccess();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this._currentMode = FormMode.View;
      this.userPermsViewMode = this._currentMode;

      if (this.user && this.user.originalRole) {
        this.selectedRole = this.user.originalRole;
      }
    }

    this.reloadUserAccess();
  }

  private reloadUserAccess() {
    if (this.user != null && this.user != undefined && this.user.id != '') {
      if (
        this.allowImpersonate &&
        this.storageService.getCurrentUser()?.id == this.user?.id
      ) {
        this.allowImpersonateThisUser = false;
        this.impersonateNotAllowedReason = 'You cannot impersonate yourself.';
      } else if (
        this.allowImpersonate &&
        this.storageService.getImpersonatingUser() != null
      ) {
        this.allowImpersonateThisUser = false;
        this.impersonateNotAllowedReason =
          'You cannot impersonate a user while impersonating another user.';
      } else {
        this.allowImpersonateThisUser = true;
        this.impersonateNotAllowedReason = '';
      }

      if (this.user.accessBits) {
        this.userAccessMap = this.navService.generateAccessTable(
          this.allNamespaces,
          this.user.accessBits,
        );

        this.userAccessGrant = [];
        this.userAccessRevoke = [];

        // We basically have to build the checkbox table from a copy
        // of the namespaces map rather than use the map itself.
        // Otherwise, Angular will hold onto some sort of reference
        // to the checkbox control, and when the form gets reloaded
        // for another user, all of the previous selections will be
        // reinstated, despite the result of [checked]=getChecked().
        // ANNOYING!!!!
        this.accessCategories = [];
        this.usedAccessCategories = [];
        this.allAccessBitsToCheck = [];
        for (let i = 0; i < this.allNamespaces.length; i++) {
          // TODO: ignoring the base-level permissions.
          // Should we get rid of them?

          
          if (
            this.allNamespaces[i].name != 'portal-r' &&
            this.allNamespaces[i].name != 'portal-w'
          ) {
            const { name, category, friendlyName, accessLevelConf } = this.allNamespaces[i];
            if (!this.accessCategories.includes(category)) {
              this.accessCategories.push(category);
              if(!this.usedAccessCategories.includes(category) && this.getChecked(name, category)) {
                this.usedAccessCategories.push(category);
              }
            }
            this.allAccessBitsToCheck.push({ name, category, friendlyName, accessLevelConf })
          }
        }

        this.changeDetector.detectChanges();
      } else {
        this.userAccessMap = new Map<string, boolean>();
      }

      this.merchantsTableOptions.userId = this.user?.id;
      this.merchantTableControl?.setOptions(this.merchantsTableOptions);
      this.merchantTableControl?.reload('', new SearchFilters({}), 1, 50);
    }
  }

  resetView() {
    this._currentMode = this.initialMode;
  }

  getCurrentPermissions(): string {
    let perms = '<ul>';

    for (let i = 0; i < this.allNamespaces.length; i++) {
      if (
        this.allNamespaces[i].name != 'portal-r' &&
        this.allNamespaces[i].name != 'portal-w'
      ) {
        if (
          i == 0 ||
          (i > 0 &&
            this.allNamespaces[i].category !=
              this.allNamespaces[i - 1].category)
        ) {
          perms +=
            '<div style="font-weight:bold; padding-top:10px;">' +
            this.allNamespaces[i].category +
            '</div>';
        }

        if (
          this.getChecked(
            this.allNamespaces[i].name,
            this.allNamespaces[i].category,
          )
        ) {
          perms += '<li>' + this.allNamespaces[i].friendlyName + '</li>';
        }
      }
    }

    perms += '</ul>';

    return perms;
  }

  //// UI Handlers //////

  btnCancelEditInfo_click() {
    this.resetView();

    if (!this.user) {
      // If there's no existing user, then we are in "new user" mode, so this
      // should act the same as the main cancel button
      this.cancelled.emit();
    }
  }

  btnChangeRole_click() {
    if (this.user && this.user.id) {
      this.userService
        .revertUserRole(this.user.id, this.selectedRole)
        .then((result) => {
          if (this.user) {
            this.user.originalRole = this.selectedRole;
            this.saved.emit(this.user); //TODO: change this to emit a user id instead
            //this.userFormData = User.createEmpty();
            this.userAccessGrant = [];
            this.userAccessRevoke = [];
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnCopyUserRoles_click() {
    if (
      this.user &&
      this.user.id &&
      this.sourceRolesUser &&
      this.sourceRolesUser != ''
    ) {
      this.userService
        .copyUserAccess(this.sourceRolesUser, this.user.id)
        .then((result: boolean) => {
          if (!result) {
            this.uiErrorHandler.handleError('Failed to copy user roles.');
          } else {
            this.sourceRolesUser = '';
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnCancel_click() {
    this.userPermsViewMode = FormMode.View;
    //this.userDetailsViewMode = FormMode.View;

    this.resetView();

    this.cancelled.emit();
  }

  btnSaveCustomPerms_click() {
    if (this.user && this.user.id) {
      this.userService
        .saveUserAccessBits(
          this.user.id,
          this.userAccessGrant,
          this.userAccessRevoke,
        )
        .then((response) => {
          this.saved.emit(this.user); //TODO: change this to emit a user id instead
          //this.userFormData = User.createEmpty();
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnResetPassword_click() {
    if (this.user && this.user.id) {
      this.userService
        .forcePasswordReset(this.user?.id)
        .then(() => {
          if (this.user) {
            this.user.requiresPasswordReset = true;
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnEnable_click() {
    if (this.user && this.user.id) {
      var that = this;

      this.userService
        .enableAccount(this.user.id)
        .then((response) => {
          if (that.user) {
            that.user.isDisabled = false;
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnDisable_click() {
    if (this.user && this.user.id) {
      var that = this;

      this.userService
        .disableAccount(this.user.id)
        .then((response) => {
          if (that.user) {
            that.user.isDisabled = true;
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  btnImpersonateUser_click() {
    if (this.user && this.user.id) {
      this.userService.impersonateUser(this.user.id);
    }
  }

  categoryHasCheckedItems(accessCategory: string): boolean {
    // console.log({ category: accessCategory, categoryMap: this.categoryMap, includes: this.categoryMap.includes(accessCategory), userAccessMap: this.userAccessMap });
    return this.categoryMap.includes(accessCategory);
  }

  public getUsers(param: string): Promise<ListItem[]> {
    if (param) {
      return BaseComponent.userService
        .getPaged('', '', param.trim(), 0, 10)
        .then((response) => {
          return response;
        })
        .then((page: Page<User>) => {
          return page.records.map(
            (user: User) =>
              new ListItem({
                id: user.id,
                name:
                  user.firstName +
                  ' ' +
                  user.lastName +
                  ' (' +
                  user.emailAddress +
                  ')',
              }),
          );
        });
    } else {
      throw new Error('No parameter provided');
    }
  }

  getChecked(namespace: string, category: string): boolean {
    if (this.userAccessMap) {
      let included = this.userAccessMap.get(namespace);

      if (included && !this.categoryMap.includes(category)) {
        this.categoryMap.push(category);
      }

      return included || false;
    }
    return false;
  }

  getPaddingLevels(namespace: string): number {
    return namespace.split('.').length - 1;
  }

  // TODO: possible bug here, where a bit gets added to grant first, then
  // added to revoke (but not removed from grant), and vice versa.
  toggleAccess(event: any, namespace: string) {
    if (event.srcElement.checked) {
      if (!this.userAccessGrant.includes(namespace)) {
        this.userAccessGrant.push(namespace);
      }
    } else {
      if (!this.userAccessRevoke.includes(namespace)) {
        this.userAccessRevoke.push(namespace);
      }
    }
  }

  getFloatLabelValue(): FloatLabelType {
    return 'auto';
  }

  // TODO: move this to constants
  getRoleName(role: string | undefined): string {
    switch (role) {
      case 'portal.support':
        return 'Support Team';
      case 'portal.manager':
        return 'Portal Manager';
      default:
        return role ? role : '';
    }
  }
}
